import { ensureSkinFonts } from './fonts';
import { useCallback, useEffect, useState } from 'react';

/**
 * Display preferences, applied as attributes on the document root so `theme.css`
 * can respond without any component knowing about them.
 *
 * Motion defaults to `system`, which leaves the existing `prefers-reduced-motion`
 * handling in charge; the toggle only exists for people whose device setting does
 * not match what they want here.
 */

export interface A11ySettings {
  /** `cb` swaps the green/red pair for blue/orange. */
  palette: 'neon' | 'cb';
  textSize: 'normal' | 'large';
  motion: 'system' | 'reduced';
  /** Buzz on your turn, as the timer runs out, and at reveals. */
  haptics: 'on' | 'off';
  /** Alerts is the old behaviour: a chime on your turn and nothing else. */
  sound: 'off' | 'alerts' | 'all';
}

export const DEFAULT_A11Y: A11ySettings = {
  palette: 'neon',
  textSize: 'normal',
  motion: 'system',
  haptics: 'on',
  sound: 'alerts',
};

const KEY = 'ofc:a11y';

/** The saved preferences, read fresh, for code that is not a component. */
export function readA11y(): A11ySettings {
  return load();
}

function load(): A11ySettings {
  try {
    const raw = localStorage.getItem(KEY);
    return raw ? { ...DEFAULT_A11Y, ...(JSON.parse(raw) as Partial<A11ySettings>) } : DEFAULT_A11Y;
  } catch {
    return DEFAULT_A11Y;
  }
}

function apply(settings: A11ySettings): void {
  const root = document.documentElement;
  root.dataset.palette = settings.palette;
  root.dataset.textSize = settings.textSize;
  root.dataset.motion = settings.motion;
}

const SKIN_KEY = 'ofc:skin';

/** Applies the stored preferences before React paints, to avoid a flash. */
export function initA11y(): void {
  apply(load());
  // The skin lives on the account, but a copy is kept here so a returning
  // player's own look is on screen from the first frame rather than after sign-in.
  try {
    const skin = localStorage.getItem(SKIN_KEY);
    if (skin) document.documentElement.dataset.skin = skin;
    ensureSkinFonts(skin);
  } catch {
    // Storage blocked; the account's skin arrives once they are signed in.
  }
}

/**
 * Puts a skin on this phone. Only the player who chose it ever sees it: nothing
 * here touches what is sent to the room.
 */
export function applySkin(skin: string): void {
  const root = document.documentElement;
  if (skin === 'neon') delete root.dataset.skin;
  else root.dataset.skin = skin;
  ensureSkinFonts(skin);
  try {
    localStorage.setItem(SKIN_KEY, skin);
  } catch {
    // Storage blocked; it still applies until the page is closed.
  }
}

export function useA11y() {
  const [settings, setSettings] = useState<A11ySettings>(load);

  useEffect(() => {
    apply(settings);
    try {
      localStorage.setItem(KEY, JSON.stringify(settings));
    } catch {
      // Storage blocked; the choice still applies for this session.
    }
  }, [settings]);

  const update = useCallback((patch: Partial<A11ySettings>) => {
    setSettings((prev) => ({ ...prev, ...patch }));
  }, []);

  return { settings, update };
}
